import { createContext, Dispatch, SetStateAction } from 'react'
import { Tables } from '@/supabase/types'
import {
  Application,
  ChatFile,
  ChatActivityStatus,
  ChatMessage,
  ChatSettings,
  LLM,
  MessageImage,
  OpenRouterLLM,
  WorkspaceImage
} from '@/types'

import { AssistantImage } from '@/types/images/assistant-image'
import type { SidecarChatStatus } from '@/types/sidecar'
import type {
  SelectedSpaceItem,
  SpaceMemberWithProfile,
  SpaceNote
} from '@/types/spaces'
import { VALID_ENV_KEYS } from '@/types/valid-keys'

export interface AttachmentScopedState {
  chatFiles: ChatFile[]
  chatImages: MessageImage[]
  newMessageFiles: ChatFile[]
  newMessageImages: MessageImage[]
  showFilesDisplay: boolean
}

export interface ScopedAttachmentBindings {
  getSnapshot: () => AttachmentScopedState
  setChatFiles: Dispatch<SetStateAction<ChatFile[]>>
  setChatImages: Dispatch<SetStateAction<MessageImage[]>>
  setNewMessageFiles: Dispatch<SetStateAction<ChatFile[]>>
  setNewMessageImages: Dispatch<SetStateAction<MessageImage[]>>
  setShowFilesDisplay: Dispatch<SetStateAction<boolean>>
}

interface ChatbotUIContext {
  isContextInitialized: boolean
  applications: Application[]
  setApplications: React.Dispatch<React.SetStateAction<Application[]>>

  // PROFILE STORE
  profile: Tables<'profiles'> | null
  setProfile: Dispatch<SetStateAction<Tables<'profiles'> | null>>

  // ITEMS STORE
  assistants: Tables<'assistants'>[]
  setAssistants: Dispatch<SetStateAction<Tables<'assistants'>[]>>
  collections: Tables<'collections'>[]
  setCollections: Dispatch<SetStateAction<Tables<'collections'>[]>>
  chats: Tables<'chats'>[]
  setChats: Dispatch<SetStateAction<Tables<'chats'>[]>>
  hasMoreChats: boolean
  isLoadingMoreChats: boolean
  loadMoreChats: () => Promise<void>
  files: Tables<'files'>[]
  setFiles: Dispatch<SetStateAction<Tables<'files'>[]>>
  folders: Tables<'folders'>[]
  setFolders: Dispatch<SetStateAction<Tables<'folders'>[]>>
  models: Tables<'models'>[]
  setModels: Dispatch<SetStateAction<Tables<'models'>[]>>
  presets: Tables<'presets'>[]
  setPresets: Dispatch<SetStateAction<Tables<'presets'>[]>>
  prompts: Tables<'prompts'>[]
  setPrompts: Dispatch<SetStateAction<Tables<'prompts'>[]>>
  tools: Tables<'tools'>[]
  setTools: Dispatch<SetStateAction<Tables<'tools'>[]>>
  platformTools: Tables<'tools'>[]
  setPlatformTools: Dispatch<SetStateAction<Tables<'tools'>[]>>
  workspaces: Tables<'workspaces'>[]
  setWorkspaces: Dispatch<SetStateAction<Tables<'workspaces'>[]>>
  allModels: LLM[]
  setAllModels: Dispatch<SetStateAction<LLM[]>>

  // MODELS STORE
  envKeyMap: Record<string, VALID_ENV_KEYS>
  setEnvKeyMap: Dispatch<SetStateAction<Record<string, VALID_ENV_KEYS>>>
  availableHostedModels: LLM[]
  setAvailableHostedModels: Dispatch<SetStateAction<LLM[]>>
  availableLocalModels: LLM[]
  setAvailableLocalModels: Dispatch<SetStateAction<LLM[]>>
  availableOpenRouterModels: OpenRouterLLM[]
  setAvailableOpenRouterModels: Dispatch<SetStateAction<OpenRouterLLM[]>>

  // WORKSPACE STORE
  selectedWorkspace: Tables<'workspaces'> | null
  setSelectedWorkspace: Dispatch<SetStateAction<Tables<'workspaces'> | null>>
  refreshAccessibleWorkspaces: (
    targetWorkspaceId?: string | null
  ) => Promise<Tables<'workspaces'>[]>
  workspaceImages: WorkspaceImage[]
  setWorkspaceImages: Dispatch<SetStateAction<WorkspaceImage[]>>

  // PRESET STORE
  selectedPreset: Tables<'presets'> | null
  setSelectedPreset: Dispatch<SetStateAction<Tables<'presets'> | null>>

  // ASSISTANT STORE
  selectedAssistant: Tables<'assistants'> | null
  setSelectedAssistant: Dispatch<SetStateAction<Tables<'assistants'> | null>>
  assistantImages: AssistantImage[]
  setAssistantImages: Dispatch<SetStateAction<AssistantImage[]>>
  openaiAssistants: any[]
  setOpenaiAssistants: Dispatch<SetStateAction<any[]>>

  // PASSIVE CHAT STORE
  chatMessages: ChatMessage[]
  setChatMessages: Dispatch<SetStateAction<ChatMessage[]>>
  chatSettings: ChatSettings | null
  setChatSettings: Dispatch<SetStateAction<ChatSettings>>
  selectedChat: Tables<'chats'> | null
  setSelectedChat: Dispatch<SetStateAction<Tables<'chats'> | null>>
  chatFileItems: Tables<'file_items'>[]
  setChatFileItems: Dispatch<SetStateAction<Tables<'file_items'>[]>>

  // ACTIVE CHAT STORE
  abortController: AbortController | null
  setAbortController: Dispatch<SetStateAction<AbortController | null>>
  firstTokenReceived: boolean
  setFirstTokenReceived: Dispatch<SetStateAction<boolean>>
  isGenerating: boolean
  setIsGenerating: Dispatch<SetStateAction<boolean>>

  // CHAT INPUT COMMAND STORE
  isMessageHistoryPickerOpen: boolean
  setIsMessageHistoryPickerOpen: Dispatch<SetStateAction<boolean>>
  isPromptPickerOpen: boolean
  setIsPromptPickerOpen: Dispatch<SetStateAction<boolean>>
  slashCommand: string
  setSlashCommand: Dispatch<SetStateAction<string>>
  isFilePickerOpen: boolean
  setIsFilePickerOpen: Dispatch<SetStateAction<boolean>>
  hashtagCommand: string
  setHashtagCommand: Dispatch<SetStateAction<string>>
  isToolPickerOpen: boolean
  setIsToolPickerOpen: Dispatch<SetStateAction<boolean>>
  toolCommand: string
  setToolCommand: Dispatch<SetStateAction<string>>
  focusPrompt: boolean
  setFocusPrompt: Dispatch<SetStateAction<boolean>>
  focusFile: boolean
  setFocusFile: Dispatch<SetStateAction<boolean>>
  focusTool: boolean
  setFocusTool: Dispatch<SetStateAction<boolean>>
  focusAssistant: boolean
  setFocusAssistant: Dispatch<SetStateAction<boolean>>
  atCommand: string
  setAtCommand: Dispatch<SetStateAction<string>>
  isAssistantPickerOpen: boolean
  setIsAssistantPickerOpen: Dispatch<SetStateAction<boolean>>
  promptVariables: {
    promptId: string
    name: string
    value: string
  }[]
  setPromptVariables: Dispatch<
    SetStateAction<
      {
        promptId: string
        name: string
        value: string
      }[]
    >
  >
  showPromptVariables: boolean
  setShowPromptVariables: Dispatch<SetStateAction<boolean>>

  // ATTACHMENTS STORE
  chatFiles: ChatFile[]
  setChatFiles: Dispatch<SetStateAction<ChatFile[]>>
  chatImages: MessageImage[]
  setChatImages: Dispatch<SetStateAction<MessageImage[]>>
  newMessageFiles: ChatFile[]
  setNewMessageFiles: Dispatch<SetStateAction<ChatFile[]>>
  newMessageImages: MessageImage[]
  setNewMessageImages: Dispatch<SetStateAction<MessageImage[]>>
  showFilesDisplay: boolean
  setShowFilesDisplay: Dispatch<SetStateAction<boolean>>
  activeAttachmentKey: string
  activeAttachmentOverrideId: string | null
  setActiveAttachmentOverrideId: Dispatch<SetStateAction<string | null>>
  getAttachmentStoreKey: (chatId?: string | null) => string
  getScopedAttachmentBindings: (attachmentKey: string) => ScopedAttachmentBindings
  migrateAttachmentState: (fromAttachmentKey: string, toAttachmentKey: string) => void

  // RETRIEVAL STORE
  useRetrieval: boolean
  setUseRetrieval: Dispatch<SetStateAction<boolean>>
  sourceCount: number
  setSourceCount: Dispatch<SetStateAction<number>>

  // TOOL STORE
  selectedTools: Tables<'tools'>[]
  setSelectedTools: Dispatch<SetStateAction<Tables<'tools'>[]>>
  toolInUse: string
  setToolInUse: Dispatch<SetStateAction<string>>

  // PAYWALL
  isPaywallOpen: boolean
  setIsPaywallOpen: Dispatch<SetStateAction<boolean>>

  // PROFILE SETTINGS
  isProfileSettingsOpen: string
  setIsProfileSettingsOpen: Dispatch<SetStateAction<string>>

  // SIDEBAR
  showSidebar: boolean
  setShowSidebar: Dispatch<SetStateAction<boolean>>

  // CREATE PROMPT (from prompt picker / sidebar)
  isCreatePromptOpen: boolean
  setIsCreatePromptOpen: Dispatch<SetStateAction<boolean>>

  // THINK DEEPER
  isThinkDeeperEnabled: boolean
  setIsThinkDeeperEnabled: Dispatch<SetStateAction<boolean>>

  // SPACE NOTES (Nexus notes page)
  spaceNotes: SpaceNote[]
  setSpaceNotes: Dispatch<SetStateAction<SpaceNote[]>>

  // SPACES STORE
  spaces: Tables<'spaces'>[]
  setSpaces: Dispatch<SetStateAction<Tables<'spaces'>[]>>
  isLoadingSpaces: boolean
  setIsLoadingSpaces: Dispatch<SetStateAction<boolean>>
  selectedSpace: Tables<'spaces'> | null
  setSelectedSpace: Dispatch<SetStateAction<Tables<'spaces'> | null>>
  spaceMembers: SpaceMemberWithProfile[]
  setSpaceMembers: Dispatch<SetStateAction<SpaceMemberWithProfile[]>>
  selectedSpaceItem: SelectedSpaceItem
  setSelectedSpaceItem: Dispatch<SetStateAction<SelectedSpaceItem>>
  chatActivityStatuses: Record<string, ChatActivityStatus>
  setChatActivityStatuses: Dispatch<SetStateAction<Record<string, ChatActivityStatus>>>
  markChatActivitySeen: (chatId: string, options?: { requestId?: string }) => void
  sidecarChatStatuses: Record<string, SidecarChatStatus>
  setSidecarChatStatuses: Dispatch<SetStateAction<Record<string, SidecarChatStatus>>>
  markSidecarChatSeen: (chatId: string, options?: { runId?: string }) => void
}

export const ChatbotUIContext = createContext<ChatbotUIContext>({
  isContextInitialized: false,
  applications: [],
  setApplications: () => {},

  // PROFILE STORE
  profile: null,
  setProfile: () => {},

  // ITEMS STORE
  assistants: [],
  setAssistants: () => {},
  collections: [],
  setCollections: () => {},
  chats: [],
  setChats: () => {},
  hasMoreChats: false,
  isLoadingMoreChats: false,
  loadMoreChats: async () => {},
  files: [],
  setFiles: () => {},
  folders: [],
  setFolders: () => {},
  models: [],
  setModels: () => {},
  presets: [],
  setPresets: () => {},
  prompts: [],
  setPrompts: () => {},
  tools: [],
  setTools: () => {},
  platformTools: [],
  setPlatformTools: () => {},
  workspaces: [],
  setWorkspaces: () => {},

  // MODELS STORE
  envKeyMap: {},
  setEnvKeyMap: () => {},
  availableHostedModels: [],
  setAvailableHostedModels: () => {},
  availableLocalModels: [],
  setAvailableLocalModels: () => {},
  availableOpenRouterModels: [],
  setAvailableOpenRouterModels: () => {},
  allModels: [],
  setAllModels: () => {},

  // WORKSPACE STORE
  selectedWorkspace: null,
  setSelectedWorkspace: () => {},
  refreshAccessibleWorkspaces: async () => [],
  workspaceImages: [],
  setWorkspaceImages: () => {},

  // PRESET STORE
  selectedPreset: null,
  setSelectedPreset: () => {},

  // ASSISTANT STORE
  selectedAssistant: null,
  setSelectedAssistant: () => {},
  assistantImages: [],
  setAssistantImages: () => {},
  openaiAssistants: [],
  setOpenaiAssistants: () => {},

  // PASSIVE CHAT STORE
  selectedChat: null,
  setSelectedChat: () => {},
  chatMessages: [],
  setChatMessages: () => {},
  chatSettings: null,
  setChatSettings: () => {},
  chatFileItems: [],
  setChatFileItems: () => {},

  // ACTIVE CHAT STORE
  isGenerating: false,
  setIsGenerating: () => {},
  firstTokenReceived: false,
  setFirstTokenReceived: () => {},
  abortController: null,
  setAbortController: () => {},

  // CHAT INPUT COMMAND STORE
  isMessageHistoryPickerOpen: false,
  setIsMessageHistoryPickerOpen: () => {},
  isPromptPickerOpen: false,
  setIsPromptPickerOpen: () => {},
  slashCommand: '',
  setSlashCommand: () => {},
  isFilePickerOpen: false,
  setIsFilePickerOpen: () => {},
  hashtagCommand: '',
  setHashtagCommand: () => {},
  isToolPickerOpen: false,
  setIsToolPickerOpen: () => {},
  toolCommand: '',
  setToolCommand: () => {},
  focusPrompt: false,
  setFocusPrompt: () => {},
  focusFile: false,
  setFocusFile: () => {},
  focusTool: false,
  setFocusTool: () => {},
  focusAssistant: false,
  setFocusAssistant: () => {},
  atCommand: '',
  setAtCommand: () => {},
  isAssistantPickerOpen: false,
  setIsAssistantPickerOpen: () => {},
  promptVariables: [],
  setPromptVariables: () => {},
  showPromptVariables: false,
  setShowPromptVariables: () => {},

  // ATTACHMENTS STORE
  chatFiles: [],
  setChatFiles: () => {},
  chatImages: [],
  setChatImages: () => {},
  newMessageFiles: [],
  setNewMessageFiles: () => {},
  newMessageImages: [],
  setNewMessageImages: () => {},
  showFilesDisplay: false,
  setShowFilesDisplay: () => {},
  activeAttachmentKey: 'chat:draft',
  activeAttachmentOverrideId: null,
  setActiveAttachmentOverrideId: () => {},
  getAttachmentStoreKey: () => 'chat:draft',
  getScopedAttachmentBindings: () => ({
    getSnapshot: () => ({
      chatFiles: [],
      chatImages: [],
      newMessageFiles: [],
      newMessageImages: [],
      showFilesDisplay: false
    }),
    setChatFiles: () => {},
    setChatImages: () => {},
    setNewMessageFiles: () => {},
    setNewMessageImages: () => {},
    setShowFilesDisplay: () => {}
  }),
  migrateAttachmentState: () => {},

  // RETRIEVAL STORE
  useRetrieval: false,
  setUseRetrieval: () => {},
  sourceCount: 4,
  setSourceCount: () => {},

  // TOOL STORE
  selectedTools: [],
  setSelectedTools: () => {},
  toolInUse: 'none',
  setToolInUse: () => {},

  // PAYWALL
  isPaywallOpen: false,
  setIsPaywallOpen: () => {},

  // PROFILE SETTINGS
  isProfileSettingsOpen: '',
  setIsProfileSettingsOpen: () => {},

  // SIDEBAR
  showSidebar: false,
  setShowSidebar: () => {},

  // CREATE PROMPT
  isCreatePromptOpen: false,
  setIsCreatePromptOpen: () => {},

  // THINK DEEPER
  isThinkDeeperEnabled: false,
  setIsThinkDeeperEnabled: () => {},

  // SPACE NOTES (Nexus notes page)
  spaceNotes: [],
  setSpaceNotes: () => {},

  // SPACES STORE
  spaces: [],
  setSpaces: () => {},
  isLoadingSpaces: false,
  setIsLoadingSpaces: () => {},
  selectedSpace: null,
  setSelectedSpace: () => {},
  spaceMembers: [],
  setSpaceMembers: () => {},
  selectedSpaceItem: null,
  setSelectedSpaceItem: () => {},
  chatActivityStatuses: {},
  setChatActivityStatuses: () => {},
  markChatActivitySeen: () => {},
  sidecarChatStatuses: {},
  setSidecarChatStatuses: () => {},
  markSidecarChatSeen: () => {}
})
